import {makeAutoObservable, runInAction} from "mobx";
import {rootContainer, TYPE} from "../../../../services/container/root";
import {isSet} from "../../../../models/model-types-methods";
import AdminManageMerchantsMarginsService from "../../../../services/api/admin-manage-merchants-margins-service";
import SbrMarginGroupsEditorStore from "../../sbr-margin-groups/stores/sbr-margin-groups-editor-store";
import {ISbrMarginGroup} from "../../sbr-margin-groups/types";
import {ICheckboxesTreeItem} from "../../../../components/inputs/checkboxes-tree-dropdown/checkboxes-tree/stores/checkboxes-tree-store";
import {IShipmentOptionsForSelectionResponseDto} from "../../../return-methods/return-method-types";

export class ManageMerchantSbrMarginsStore {
    private _merchantId?: number;
    private _editorStore?: SbrMarginGroupsEditorStore;
    private _shipmentOptions: ICheckboxesTreeItem[] = [];
    private _isLoading = false;
    private _isLoadingOptions = false;
    private _isSaving = false;
    private _optionsLoadPromise?: Promise<void>;

    constructor() {
        makeAutoObservable(this, {}, {autoBind: true});
    }

    private get adminManageMerchantsMarginsService(): AdminManageMerchantsMarginsService {
        return rootContainer.get<AdminManageMerchantsMarginsService>(TYPE.AdminManageMerchantsMarginsService);
    }

    get isLoading(): boolean {
        return this._isLoading;
    }

    get isSaving(): boolean {
        return this._isSaving;
    }

    public isLoadingOptions(): boolean {
        return this._isLoadingOptions;
    }

    public getEditorStore(): SbrMarginGroupsEditorStore | undefined {
        return this._editorStore;
    }

    public hasChanges(): boolean {
        return this._editorStore?.hasChanges() ?? false;
    }

    public hasInvalidMarginGroups(): boolean {
        const editorStore = this._editorStore;
        if (!editorStore) {
            return false;
        }

        return editorStore.hasDuplicateSelections() || editorStore.hasIncompleteGroups();
    }

    public clear(): void {
        this._merchantId = undefined;
        this._editorStore = undefined;
    }

    async load(merchantId: number): Promise<void> {
        if (!isSet(merchantId) || this._merchantId === merchantId) {
            return;
        }

        this._merchantId = merchantId;
        this._editorStore = undefined;

        try {
            this._isLoading = true;

            const [, response] = await Promise.all([
                this.loadShipmentOptions(),
                this.adminManageMerchantsMarginsService.fetchMerchantSbrMarginGroups(merchantId),
            ]);

            runInAction(() => {
                this._editorStore = this.createEditorStore(response.sbr_margin_groups ?? []);
            });
        } catch (e) {}
        finally {
            runInAction(() => {
                this._isLoading = false;
            });
        }
    }

    async save(): Promise<void> {
        if (!isSet(this._merchantId) || !this.hasChanges() || !this._editorStore) {
            return;
        }

        this._isSaving = true;

        try {
            const response = await this.adminManageMerchantsMarginsService.updateMerchantSbrMarginGroups(
                this._merchantId as number,
                this._editorStore.getUpdatedModel()
            );

            runInAction(() => {
                this._editorStore?.reset(response.sbr_margin_groups ?? []);
            });
        } catch (e) {}
        finally {
            runInAction(() => {
                this._isSaving = false;
            });
        }
    }

    public async loadShipmentOptions(): Promise<void> {
        if (this._shipmentOptions.length) {
            return;
        }

        if (this._optionsLoadPromise) {
            return this._optionsLoadPromise;
        }

        this._isLoadingOptions = true;
        this._optionsLoadPromise = this.adminManageMerchantsMarginsService.fetchSbrShipmentOptions()
            .then((shipmentOptions) => {
                runInAction(() => {
                    this._shipmentOptions = this.transformShipmentDtoToOptions(shipmentOptions);
                    this._isLoadingOptions = false;
                    this._optionsLoadPromise = undefined;
                });
            })
            .catch(() => {
                runInAction(() => {
                    this._isLoadingOptions = false;
                    this._optionsLoadPromise = undefined;
                });
            });

        return this._optionsLoadPromise;
    }

    private createEditorStore(groups: ISbrMarginGroup[]): SbrMarginGroupsEditorStore {
        return new SbrMarginGroupsEditorStore(this._shipmentOptions, groups);
    }

    private transformShipmentDtoToOptions(
        carriersOptionsList: IShipmentOptionsForSelectionResponseDto
    ): ICheckboxesTreeItem[] {
        return carriersOptionsList.list
            .map((carrier) => ({
                label: carrier.carrierName,
                value: carrier.carrierCode,
                childNodes: carrier.list.map((service) => ({
                    label: service.serviceName,
                    value: service.serviceCode,
                })),
            }))
            .sort((optionA, optionB) => {
                return optionA.label.localeCompare(optionB.label);
            });
    }
}

export default new ManageMerchantSbrMarginsStore();
